import type { TrackerPitchMode } from "./model.js";
import { trackerNoteForKeyCode } from "./note-data.js";

export type TrackerKeyboardContext = {
  changeOctave(delta: number): void;
  clearSelection(): void;
  copySelection(): void;
  moveSelection(deltaRow: number, deltaChannel: number, extend: boolean): void;
  noteDown(note: number): void;
  noteUp(note: number): void;
  octave: number;
  pitchMode: TrackerPitchMode;
  pasteSelection(): void;
  redo(): void;
  setSelection(row: number, channel: number, extend: boolean): void;
  selectionChannel: number;
  selectionRow: number;
  transposeSelection(delta: number): void;
  undo(): void;
};

export function handleTrackerKeyDown(event: KeyboardEvent, context: TrackerKeyboardContext): boolean {
  const note = trackerNoteForKeyCode(event.code, context.octave, context.pitchMode);
  if (note !== null && !event.altKey && !event.ctrlKey && !event.metaKey) {
    context.noteDown(note);
    return true;
  }

  if (event.metaKey) {
    return false;
  }

  if (event.ctrlKey) {
    switch (event.code) {
      case "KeyC":
        context.copySelection();
        return true;
      case "KeyV":
        context.pasteSelection();
        return true;
      case "KeyY":
        context.redo();
        return true;
      case "KeyZ":
        if (event.shiftKey) {
          context.redo();
        } else {
          context.undo();
        }
        return true;
      default:
        return false;
    }
  }

  switch (event.code) {
    case "ArrowUp":
      context.moveSelection(-1, 0, event.shiftKey);
      return true;
    case "ArrowDown":
      context.moveSelection(1, 0, event.shiftKey);
      return true;
    case "ArrowLeft":
      context.moveSelection(0, -1, event.shiftKey);
      return true;
    case "ArrowRight":
      context.moveSelection(0, 1, event.shiftKey);
      return true;
    case "Tab":
      context.moveSelection(0, event.shiftKey ? -1 : 1, false);
      return true;
    case "Home":
      context.setSelection(0, context.selectionChannel, event.shiftKey);
      return true;
    case "End":
      context.setSelection(63, context.selectionChannel, event.shiftKey);
      return true;
    case "Backspace":
    case "Delete":
      context.clearSelection();
      return true;
    case "BracketLeft":
      context.transposeSelection(event.shiftKey ? -12 : -1);
      return true;
    case "BracketRight":
      context.transposeSelection(event.shiftKey ? 12 : 1);
      return true;
    case "Minus":
      context.changeOctave(-1);
      return true;
    case "Equal":
      context.changeOctave(1);
      return true;
    default:
      return false;
  }
}

export function handleTrackerKeyUp(event: KeyboardEvent, context: TrackerKeyboardContext): boolean {
  const note = trackerNoteForKeyCode(event.code, context.octave, context.pitchMode);
  if (note === null) {
    return false;
  }
  context.noteUp(note);
  return true;
}
